import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useVehicles, useVehicleTemplates } from '../../../hooks';
import {
  VehicleType,
  VehicleCondition,
  CurrencyType,
  CreateVehicleDto,
  FuelType,
  TransmissionType,
  VehicleTemplate
} from '../../../types';
import styles from './AddVehicle.module.css';
import { PageTitle, ImageUpload, PriceInput } from '../../../components/common';
import { imageService } from '../../../services/api';
import { isRejected } from '@reduxjs/toolkit';

export const AddVehicle: React.FC = () => {
  const navigate = useNavigate();
  const { addVehicle, isLoading } = useVehicles();
  const { templates, loadTemplates } = useVehicleTemplates();

  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);

  React.useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const [formData, setFormData] = useState({
    marca: '',
    tipo: VehicleType.AUTO,
    modelo: '',
    anio: new Date().getFullYear(),
    km: 0,
    chassis: '',
    motor: '',
    dominio: '',
    color: '',
    fechaIngreso: new Date().toISOString().split('T')[0],
    vendedorResponsable: '',
    observacion: '',
    precio: 0,
    stock: 1,
    moneda: CurrencyType.USD,
    condicion: VehicleCondition.NUEVO,
    publicationLink: '',
    vehicleImageLink: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const filteredTemplates = React.useMemo(() => {
    if (!searchTerm) {
      return [...templates]
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 10);
    }

    const term = searchTerm.toLowerCase();
    return templates.filter(t =>
      t.name.toLowerCase().includes(term) ||
      t.brand.toLowerCase().includes(term) ||
      t.model.toLowerCase().includes(term) ||
      t.year.toString().includes(term) ||
      t.engine.toLowerCase().includes(term) ||
      t.color.toLowerCase().includes(term)
    );
  }, [templates, searchTerm]);

  const handleTemplateSelect = (template: VehicleTemplate) => {
    setFormData(prev => ({
      ...prev,
      marca: template.brand,
      modelo: template.model,
      anio: template.year,
      precio: template.price,
      color: template.color,
      motor: template.engine,
      moneda: template.currency,
      condicion: template.condition,
      tipo: template.vehicleType,
      observacion: template.description || '',
    }));
    setSearchTerm('');
    setShowDropdown(false);
    toast.success(`Datos cargados desde: ${template.name}`);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // Para campos numéricos, convertir a número o mantener vacío
    let processedValue: string | number = value;
    if (name === 'anio' || name === 'km' || name === 'precio' || name === 'stock') {
      processedValue = value === '' ? 0 : Number(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Limpiar error del campo al escribir
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleNumberInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Permitir: backspace, delete, tab, escape, enter, punto decimal (para precio)
    if (
      e.key === 'Backspace' ||
      e.key === 'Delete' ||
      e.key === 'Tab' ||
      e.key === 'Escape' ||
      e.key === 'Enter' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight' ||
      e.key === 'Home' ||
      e.key === 'End' ||
      (e.key === '.' && e.currentTarget.name === 'precio')
    ) {
      return;
    }

    // Bloquear si no es un número
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const scrollToError = (fieldName: string) => {
    // Buscar el elemento input/select/textarea con el nombre del campo
    const element = document.getElementById(fieldName);

    if (element) {
      // Scroll suave al elemento con un offset para que no quede pegado al top
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      // Hacer foco en el elemento después de un pequeño delay
      setTimeout(() => {
        element.focus();
      }, 300);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    let firstErrorField: string | null = null;

    if (!formData.marca.trim()) {
      newErrors.marca = 'La marca es requerida';
      if (!firstErrorField) firstErrorField = 'marca';
    }
    if (!formData.modelo.trim()) {
      newErrors.modelo = 'El modelo es requerido';
      if (!firstErrorField) firstErrorField = 'modelo';
    }
    if (formData.anio < 1900 || formData.anio > new Date().getFullYear() + 1) {
      newErrors.anio = 'Año inválido';
      if (!firstErrorField) firstErrorField = 'anio';
    }
    if (formData.km < 0) {
      newErrors.km = 'Los kilómetros no pueden ser negativos';
      if (!firstErrorField) firstErrorField = 'km';
    }
    if (!formData.chassis.trim()) {
      newErrors.chassis = 'El número de chasis es requerido';
      if (!firstErrorField) firstErrorField = 'chassis';
    }
    if (!formData.motor.trim()) {
      newErrors.motor = 'El número de motor es requerido';
      if (!firstErrorField) firstErrorField = 'motor';
    }
    if (!formData.dominio.trim()) {
      newErrors.dominio = 'El dominio es requerido';
      if (!firstErrorField) firstErrorField = 'dominio';
    }
    if (!formData.color.trim()) {
      newErrors.color = 'El color es requerido';
      if (!firstErrorField) firstErrorField = 'color';
    }
    if (!formData.fechaIngreso) {
      newErrors.fechaIngreso = 'La fecha de ingreso es requerida';
      if (!firstErrorField) firstErrorField = 'fechaIngreso';
    }
    if (formData.precio <= 0) {
      newErrors.precio = 'El precio debe ser mayor a 0';
      if (!firstErrorField) firstErrorField = 'precio';
    }
    if (formData.stock < 1) {
      newErrors.stock = 'El stock debe ser al menos 1';
      if (!firstErrorField) firstErrorField = 'stock';
    }

    setErrors(newErrors);

    // Si hay errores, hacer scroll al primero
    if (firstErrorField) {
      scrollToError(firstErrorField);
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor, completa todos los campos requeridos');
      return;
    }

    // Mostrar toast de carga
    const loadingToast = toast.loading('Guardando vehículo...');

    try {
      let imageUrl = formData.vehicleImageLink;

      // Si hay un archivo seleccionado
      if (file) {
        // Si ya tenemos una URL de imagen subida (de un intento anterior fallido) y el archivo no ha cambiado
        if (uploadedImageUrl) {
          imageUrl = uploadedImageUrl;
        } else {
          // Si es un archivo nuevo o no se ha subido aún
          toast.loading('Subiendo imagen...', { id: loadingToast });
          const uploadedKey = await imageService.uploadSingle(file);
          imageUrl = imageService.getImageUrl(uploadedKey);
          setUploadedImageUrl(imageUrl); // Guardar para reintentos si falla el guardado del vehículo
        }
      }

      // Mapear los datos del formulario (español) al DTO (inglés)
      const vehicleData: CreateVehicleDto = {
        brand: formData.marca,
        model: formData.modelo,
        year: formData.anio,
        price: formData.precio,
        mileage: formData.km,
        color: formData.color,
        vin: formData.chassis,
        engine: formData.motor,
        description: formData.observacion,
        stock: formData.stock,
        currency: formData.moneda,
        condition: formData.condicion,
        vehicleType: formData.tipo,
        licensePlate: formData.dominio,
        salesperson: formData.vendedorResponsable,
        entryDate: formData.fechaIngreso,
        publicationLink: formData.publicationLink,
        vehicleImageLink: imageUrl,
        // Valores por defecto para campos requeridos que no están en el formulario
        fuelType: FuelType.GASOLINE,
        transmission: TransmissionType.MANUAL,
        doors: 4,
        seats: 5,
        images: [],
        features: []
      };

      const addVehicleResponse = await addVehicle(vehicleData);
      console.log(`addVehicleResponse: ${JSON.stringify(addVehicleResponse)}`)
      if (isRejected(addVehicleResponse)) {
        toast.error(addVehicleResponse?.payload as string || 'Error al agregar el vehículo');
        return;
      } else {
        // Cerrar toast de carga y mostrar éxito
        toast.dismiss(loadingToast);
        toast.success('¡Vehículo agregado exitosamente!', {
          icon: '✅',
        });

        // Navegar después de un pequeño delay para que el usuario vea el toast
        setTimeout(() => {
          navigate('/inventory');
        }, 1000);
      }

    } catch (error: any) {
      console.error('Error al agregar vehículo:', error);

      // Cerrar toast de carga y mostrar error
      toast.dismiss(loadingToast);
      toast.error(error?.message || 'Error al agregar el vehículo. Por favor, intenta nuevamente.', {
        duration: 5000,
      });
    }
  };

  const handleCancel = () => {
    // Verificar si hay datos ingresados
    const hasData =
      formData.marca.trim() !== '' ||
      formData.modelo.trim() !== '' ||
      formData.chassis.trim() !== '' ||
      formData.motor.trim() !== '' ||
      formData.dominio.trim() !== '' ||
      formData.color.trim() !== '' ||
      formData.precio > 0 ||
      formData.vendedorResponsable.trim() !== '' ||
      formData.observacion?.trim() !== '' ||
      formData.publicationLink.trim() !== '' ||
      formData.vehicleImageLink.trim() !== '';

    if (hasData) {
      // Mostrar toast de confirmación con botones
      toast((t) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
            ¿Estás seguro de cancelar?
          </div>
          <div style={{ fontSize: '13px', color: '#666' }}>
            Los datos no guardados se perderán.
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                toast.success('Operación cancelada');
                navigate('/inventory');
              }}
              style={{
                flex: 1,
                padding: '8px 16px',
                background: '#C62828',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
              }}
            >
              Sí, cancelar
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              style={{
                flex: 1,
                padding: '8px 16px',
                background: '#00A6E8',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
              }}
            >
              Continuar editando
            </button>
          </div>
        </div>
      ), {
        duration: Infinity, // No se cierra automáticamente
        style: {
          minWidth: '300px',
        },
      });
    } else {
      navigate('/inventory');
    }
  };

  return (
    <>
      <PageTitle
        title="Agregar Nuevo Vehículo"
        description="Completa todos los campos para registrar un vehículo en el sistema"
      />
      <div className={styles.addVehicleContainer}>

        {/* Buscador de Plantillas */}
        <div className={styles.section} style={{ marginBottom: '15px' }}>
          <h2 className={styles.sectionTitle}>
            📝 Cargar desde Plantilla
          </h2>
          <div className={styles.templateSearchContainer}>
            <span className={styles.searchIcon}>🔍</span>
            <input
              type="text"
              className={styles.templateSearchInput}
              placeholder="Buscar plantilla por nombre, marca, modelo..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
            />

            {showDropdown && filteredTemplates.length > 0 && (
              <div className={styles.templateDropdown}>
                {filteredTemplates.map(template => (
                  <div
                    key={template.id}
                    className={styles.templateItem}
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <div className={styles.templateItemInfo}>
                      <h4>{template.name}</h4>
                      <p>{template.brand} {template.model} {template.year} - {template.engine}</p>
                    </div>
                    <div className={styles.templateItemBadges}>
                      <span className={`${styles.templateBadge} ${styles[template.condition]}`}>
                        {template.condition}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Información Básica */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>📋 Información Básica</h2>

            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="marca" className={styles.required}>
                  Marca
                </label>
                <input
                  type="text"
                  id="marca"
                  name="marca"
                  value={formData.marca}
                  onChange={handleChange}
                  placeholder="Ej: Toyota, Honda, Ford"
                  className={errors.marca ? styles.inputError : ''}
                />
                {errors.marca && <span className={styles.errorText}>{errors.marca}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="tipo" className={styles.required}>
                  Tipo de Vehículo
                </label>
                <select
                  id="tipo"
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleChange}
                >
                  <option value={VehicleType.AUTO}>Auto</option>
                  <option value={VehicleType.MOTO}>Moto</option>
                  <option value={VehicleType.CAMIONETA}>Camioneta</option>
                  <option value={VehicleType.CAMION}>Camión</option>
                  <option value={VehicleType.CUATRICICLO}>Cuatriciclo</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="modelo" className={styles.required}>
                  Modelo
                </label>
                <input
                  type="text"
                  id="modelo"
                  name="modelo"
                  value={formData.modelo}
                  onChange={handleChange}
                  placeholder="Ej: Corolla, Civic, Fiesta"
                  className={errors.modelo ? styles.inputError : ''}
                />
                {errors.modelo && <span className={styles.errorText}>{errors.modelo}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="anio" className={styles.required}>
                  Año
                </label>
                <input
                  type="number"
                  id="anio"
                  name="anio"
                  value={formData.anio}
                  onChange={handleChange}
                  onKeyDown={handleNumberInput}
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  step="1"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className={errors.anio ? styles.inputError : ''}
                />
                {errors.anio && <span className={styles.errorText}>{errors.anio}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="km" className={styles.required}>
                  Kilómetros
                </label>
                <input
                  type="text"
                  id="km"
                  name="km"
                  value={formData.km === 0 ? '' : new Intl.NumberFormat('es-AR').format(formData.km)}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\./g, '').replace(/\D/g, '');
                    const num = val === '' ? 0 : Number(val);
                    setFormData(prev => ({ ...prev, km: num }));
                    if (errors.km) {
                      setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.km;
                        return newErrors;
                      });
                    }
                  }}
                  onKeyDown={handleNumberInput}
                  inputMode="numeric"
                  placeholder="0"
                  className={errors.km ? styles.inputError : ''}
                />
                {errors.km && <span className={styles.errorText}>{errors.km}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="condicion" className={styles.required}>
                  Condición
                </label>
                <select
                  id="condicion"
                  name="condicion"
                  value={formData.condicion}
                  onChange={handleChange}
                >
                  <option value={VehicleCondition.NUEVO}>Nuevo</option>
                  <option value={VehicleCondition.USADO}>Usado</option>
                </select>
              </div>
            </div>
          </div>

          {/* Identificación Técnica */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>🔧 Identificación Técnica</h2>

            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="chassis" className={styles.required}>
                  Número de Chasis
                </label>
                <input
                  type="text"
                  id="chassis"
                  name="chassis"
                  value={formData.chassis}
                  onChange={handleChange}
                  placeholder="Ej: 9BWZZZ377VT004251"
                  className={errors.chassis ? styles.inputError : ''}
                />
                {errors.chassis && <span className={styles.errorText}>{errors.chassis}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="motor" className={styles.required}>
                  Número de Motor
                </label>
                <input
                  type="text"
                  id="motor"
                  name="motor"
                  value={formData.motor}
                  onChange={handleChange}
                  placeholder="Ej: 1NZ1234567"
                  className={errors.motor ? styles.inputError : ''}
                />
                {errors.motor && <span className={styles.errorText}>{errors.motor}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="dominio" className={styles.required}>
                  Dominio/Patente
                </label>
                <input
                  type="text"
                  id="dominio"
                  name="dominio"
                  value={formData.dominio}
                  onChange={handleChange}
                  placeholder="Ej: ABC123 o AB123CD"
                  className={errors.dominio ? styles.inputError : ''}
                />
                {errors.dominio && <span className={styles.errorText}>{errors.dominio}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="color" className={styles.required}>
                  Color
                </label>
                <input
                  type="text"
                  id="color"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  placeholder="Ej: Blanco, Negro, Azul"
                  className={errors.color ? styles.inputError : ''}
                />
                {errors.color && <span className={styles.errorText}>{errors.color}</span>}
              </div>
            </div>
          </div>

          {/* Información Comercial */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>💰 Información Comercial</h2>

            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="precio" className={styles.required}>
                  Precio
                </label>
                <div className={styles.priceInputGroup}>
                  <PriceInput
                    value={formData.precio}
                    currency={formData.moneda}
                    onChange={(val) => {
                      setFormData(prev => ({ ...prev, precio: val }));
                      if (errors.precio) {
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.precio;
                          return newErrors;
                        });
                      }
                    }}
                    onCurrencyChange={(curr) =>
                      setFormData(prev => ({ ...prev, moneda: curr }))
                    }
                    error={errors.precio}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="fechaIngreso" className={styles.required}>
                  Fecha de Ingreso
                </label>
                <input
                  type="date"
                  id="fechaIngreso"
                  name="fechaIngreso"
                  value={formData.fechaIngreso}
                  onChange={handleChange}
                  max={new Date().toISOString().split('T')[0]}
                  className={errors.fechaIngreso ? styles.inputError : ''}
                />
                {errors.fechaIngreso && (
                  <span className={styles.errorText}>{errors.fechaIngreso}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="stock" className={styles.required}>
                  Stock/Cantidad
                </label>
                <input
                  type="number"
                  id="stock"
                  name="stock"
                  value={formData.stock}
                  onChange={handleChange}
                  onKeyDown={handleNumberInput}
                  min="1"
                  step="1"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="1"
                  className={errors.stock ? styles.inputError : ''}
                />
                {errors.stock && <span className={styles.errorText}>{errors.stock}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="publicationLink">
                  Link de Publicación (Opcional)
                </label>
                <input
                  type="text"
                  id="publicationLink"
                  name="publicationLink"
                  value={formData.publicationLink}
                  onChange={handleChange}
                  placeholder="https://instagram.com/..."
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Imagen del Vehículo (Opcional)
                </label>
                <div style={{ marginTop: '0.5rem' }}>
                  <ImageUpload
                    onImageSelect={(file) => {
                      setFile(file);
                      setUploadedImageUrl(null); // Resetear subida previa si cambia el archivo
                    }}
                    previewUrl={formData.vehicleImageLink || undefined}
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="vendedorResponsable">
                  Vendedor Responsable (Opcional)
                </label>
                <input
                  type="text"
                  id="vendedorResponsable"
                  name="vendedorResponsable"
                  value={formData.vendedorResponsable}
                  onChange={handleChange}
                  placeholder="Ej: Juan Pérez"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="observacion">
                Observaciones (Opcional)
              </label>
              <textarea
                id="observacion"
                name="observacion"
                value={formData.observacion}
                onChange={handleChange}
                placeholder="Notas adicionales sobre el vehículo..."
                rows={4}
              />
            </div>
          </div>

          {/* Botones de Acción */}
          <div className={styles.formActions}>
            <button
              type="button"
              onClick={handleCancel}
              className={styles.cancelButton}
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isLoading}
            >
              {isLoading ? 'Guardando...' : '✓ Guardar Vehículo'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

